/**
 * useNukki — 스키닝 4단계의 마지막 탈출구 (설계문서 §6.1).
 *
 * 엔진 수명주기 · 포인터 · 제스처 · 렌더 루프를 전부 여기서 다룬다.
 * `<NukkiEditor>`는 이 훅 위에 기본 마크업을 얹은 얇은 껍데기일 뿐이다.
 * 그래서 4단계 지원 비용이 거의 0이다.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  NukkiEngine, toCanvasElement,
  type ExportOptions, type LassoMode, type MaskBackend, type Point,
} from '@nukki/core';

export interface ViewTransform {
  scale: number;
  x: number;
  y: number;
}

export interface UseNukkiOptions {
  backend: MaskBackend;
  workingSize?: number;
  /** 업로드 전에 막는다. 업로드를 다 하고 413을 받을 이유가 없다. */
  maxUploadBytes?: number;
  /** 기본 export 설정. `exportBlob()` 인자로 덮어쓸 수 있다. */
  exportOptions?: ExportOptions;
  /** 최대 확대 배율. 1이면 줌을 끈다. */
  maxScale?: number;
  onDone?: (blob: Blob) => void;
  onError?: (err: Error) => void;
  onLoad?: (size: { width: number; height: number }) => void;
}

const MIN_MOVE = 2;          // 올가미 점 간 최소 거리(작업 해상도 픽셀)
const TAP_SLOP = 6;          // 이만큼 안 움직이면 탭으로 본다 (화면 픽셀)
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export function useNukki(opts: UseNukkiOptions) {
  const {
    backend, workingSize, maxUploadBytes = 15 * 1024 * 1024,
    exportOptions, maxScale = 6, onDone, onError, onLoad,
  } = opts;

  /* ── 엔진 수명 ───────────────────────────────────────────────
     backend 를 의존성으로 그냥 쓰면 안 된다. 소비처가
     `useNukki({ backend: remoteBackend({...}) })` 처럼 인라인으로 넘기면
     매 렌더마다 새 객체가 되어 **엔진이 통째로 재생성되고 편집 상태가
     조용히 날아간다.** 그래서 ref 를 읽는 고정 프록시를 끼운다.
     ─────────────────────────────────────────────────────────── */
  const backendRef = useRef(backend);
  backendRef.current = backend;

  const stableBackend = useMemo<MaskBackend>(() => ({
    get kind() { return backendRef.current.kind; },
    getMask: (image, o) => backendRef.current.getMask(image, o),
  }), []);

  const engine = useMemo(
    () => new NukkiEngine(
      workingSize === undefined
        ? { backend: stableBackend }
        : { backend: stableBackend, workingSize },
    ),
    [stableBackend, workingSize],
  );

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [error, setError] = useState<{ message: string; retry?: () => void } | null>(null);
  const [lassoMode, setLassoMode] = useState<LassoMode>('replace');
  const [selectionSteps, setSelectionSteps] = useState(0);
  const [refine, setRefine] = useState(true);
  const [transform, setTransform] = useState<ViewTransform>({ scale: 1, x: 0, y: 0 });

  // 콜백을 ref 로 잡아 두면 부모가 인라인 함수를 넘겨도 효과가 재실행되지 않는다.
  const cb = useRef({ onDone, onError, onLoad });
  cb.current = { onDone, onError, onLoad };

  /* ── 렌더 루프: rAF 코얼레싱 ────────────────────────────────
     pointermove 는 프레임보다 자주 온다. 매번 그리면 낭비다.
     ─────────────────────────────────────────────────────────── */
  const raf = useRef(0);
  const preview = useRef<Point[] | null>(null);
  const antsPhase = useRef(0);

  const draw = useCallback(() => {
    raf.current = 0;
    engine.composite(antsPhase.current);
    const pts = preview.current;
    const canvas = canvasRef.current;
    if (!pts || pts.length < 2 || !canvas) return;
    // 올가미 미리보기는 장식이라 코어가 아니라 뷰의 몫이다 (Skia 호스트는 다르게 그린다).
    const g = canvas.getContext('2d');
    if (!g) return;
    g.beginPath();
    g.moveTo(pts[0]![0], pts[0]![1]);
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i]![0], pts[i]![1]);
    g.closePath();
    g.fillStyle = 'rgba(124,156,245,.15)';
    g.fill();
    g.setLineDash([8, 6]);
    g.lineWidth = Math.max(2, engine.width / 400);
    g.strokeStyle = '#7c9cf5';
    g.stroke();
    g.setLineDash([]);
  }, [engine]);

  const schedule = useCallback(() => {
    if (!raf.current) raf.current = requestAnimationFrame(draw);
  }, [draw]);

  /* ── 선택 경계선 애니메이션 ────────────────────────────────
     선택이 있는 동안만 돈다. 없을 때 rAF 를 계속 돌리면 배터리를 먹는다.
     ─────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!selectionSteps) return;
    let id = 0;
    let last = performance.now();
    const tick = (now: number) => {
      // 시간 기준으로 흘려야 프레임률이 달라도 같은 속도로 보인다.
      antsPhase.current -= (now - last) * 0.03;
      last = now;
      engine.composite(antsPhase.current);
      id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [engine, selectionSteps]);

  /* ── 엔진 이벤트 구독 + 언마운트 정리 ─────────────────────── */
  useEffect(() => {
    const sync = () => { setCanUndo(engine.canUndo); setCanRedo(engine.canRedo); };
    const onBusy = (e: Event) => setBusy((e as CustomEvent<{ busy: boolean }>).detail.busy);
    const onLoadEvt = (e: Event) => {
      setLoaded(true);
      cb.current.onLoad?.((e as CustomEvent<{ width: number; height: number }>).detail);
    };
    const onSel = (e: Event) =>
      setSelectionSteps((e as CustomEvent<{ steps: number }>).detail.steps);
    engine.addEventListener('selection', onSel);
    engine.addEventListener('change', sync);
    engine.addEventListener('busy', onBusy);
    engine.addEventListener('load', onLoadEvt);
    return () => {
      engine.removeEventListener('selection', onSel);
      engine.removeEventListener('change', sync);
      engine.removeEventListener('busy', onBusy);
      engine.removeEventListener('load', onLoadEvt);
      if (raf.current) cancelAnimationFrame(raf.current);
      raf.current = 0;
      engine.destroy();          // 언마운트에서 버퍼·in-flight 요청 반납
    };
  }, [engine]);

  /* ── 캔버스 부착 ──────────────────────────────────────────── */
  const attachCanvas = useCallback((host: HTMLElement | null) => {
    if (!host) return;
    const canvas = toCanvasElement(engine.view);
    canvasRef.current = canvas;
    if (canvas.parentElement !== host) host.prepend(canvas);
  }, [engine]);

  /* ── 오류 ─────────────────────────────────────────────────── */
  const fail = useCallback((e: unknown, retry?: () => void) => {
    const err = e instanceof Error ? e : new Error(String(e));
    setError(retry ? { message: err.message, retry } : { message: err.message });
    cb.current.onError?.(err);
  }, []);
  const clearError = useCallback(() => setError(null), []);

  /* ── 로드 ─────────────────────────────────────────────────── */
  const load = useCallback(async (file: Blob | File) => {
    if (!file) return false;
    if (file.type && !file.type.startsWith('image/')) {
      fail(new Error('이미지 파일이 아닙니다'));
      return false;
    }
    if (file.size > maxUploadBytes) {
      const mb = (maxUploadBytes / 1048576).toFixed(0);
      fail(new Error(`파일이 너무 큽니다 (${(file.size / 1048576).toFixed(1)}MB / 최대 ${mb}MB)`));
      return false;
    }
    clearError();
    try {
      await engine.load(file);
      setSelectionSteps(0);
      setTransform({ scale: 1, x: 0, y: 0 });
      engine.prefetchMask();       // 도구를 고르는 동안 미리 분석
      schedule();
      return true;
    } catch (e) {
      fail(e);
      return false;
    }
  }, [engine, maxUploadBytes, fail, clearError, schedule]);

  /* ── 편집 명령 ──────────────────────────────────────────────
     기본 흐름은 **선택 → 다듬기 → 적용**이다.
     올가미를 떼는 순간 이미지가 잘리지 않는다.
     ─────────────────────────────────────────────────────────── */

  /** 영역 자동맞춤 — AI 가 잡은 영역을 **선택**에 채운다. 아직 안 잘린다. */
  const autoSelect = useCallback(async () => {
    clearError();
    try { await engine.autoSelect(); schedule(); }
    catch (e) { fail(e, () => { void autoSelect(); }); }
  }, [engine, fail, clearError, schedule]);

  const selectLasso = useCallback(async (pts: Point[], mode: LassoMode) => {
    if (pts.length < 3) return;
    clearError();
    try { await engine.selectLasso(pts, { refine, mode }); schedule(); }
    // 획을 버리지 않는다. 사용자가 다시 그리지 않아도 되게 들고 있는다.
    catch (e) { fail(e, () => { void selectLasso(pts, mode); }); }
  }, [engine, refine, fail, clearError, schedule]);

  /** 누른 지점이 속한 덩어리만 선택한다. 배경을 누르면 아무 일도 없다. */
  const selectAt = useCallback(async (x: number, y: number, mode: LassoMode) => {
    clearError();
    try { await engine.selectAt(x, y, { mode }); schedule(); }
    catch (e) { fail(e, () => { void selectAt(x, y, mode); }); }
  }, [engine, fail, clearError, schedule]);

  /** 선택을 마스크에 반영한다. **여기서 처음 잘린다.** */
  const applySelection = useCallback(() => { engine.applySelection(); schedule(); },
    [engine, schedule]);
  const clearSelection = useCallback(() => { engine.clearSelection(); schedule(); },
    [engine, schedule]);
  const undoSelectionStep = useCallback(() => { engine.undoSelectionStep(); schedule(); },
    [engine, schedule]);

  const undo = useCallback(() => { engine.undo(); schedule(); }, [engine, schedule]);
  const redo = useCallback(() => { engine.redo(); schedule(); }, [engine, schedule]);
  const reset = useCallback(() => { engine.reset(); schedule(); }, [engine, schedule]);
  const resetView = useCallback(() => setTransform({ scale: 1, x: 0, y: 0 }), []);

  const exportBlob = useCallback(async (o?: ExportOptions) => {
    const blob = await engine.exportBlob({ ...exportOptions, ...o });
    cb.current.onDone?.(blob);
    return blob;
  }, [engine, exportOptions]);

  /* ── 포인터 · 제스처 ────────────────────────────────────────
     손가락 1개 = 그리기, 2개 = 핀치 줌/팬. 표준 에디터 관례다.
     CSS transform 을 캔버스에 걸면 getBoundingClientRect() 가 변환된 박스를
     돌려주므로 좌표 변환 식은 그대로 둬도 된다.
     ─────────────────────────────────────────────────────────── */
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const rect = useRef<DOMRect | null>(null);
  const lasso = useRef<Point[] | null>(null);
  /**
   * 제스처 중 원점에서 벗어난 **최대 거리**.
   *
   * ⚠️ "뗀 위치 − 누른 위치" 로 판정하면 안 된다 — 올가미는 시작점으로
   * 되돌아와 닫히므로 모든 닫힌 올가미가 탭으로 오인된다.
   */
  const tapFrom = useRef<{ x: number; y: number; max: number } | null>(null);
  const pinch = useRef<{ dist: number; mid: [number, number]; t: ViewTransform } | null>(null);
  const panning = useRef<{ x: number; y: number; t: ViewTransform } | null>(null);

  /* ── 확대·축소 ──────────────────────────────────────────────
     `transform-origin: 0 0` 기준.
       화면점 p 아래의 내용 c = (p - t) / s
       배율을 s' 로 바꿔도 c 가 p 에 남으려면  t' = p - c * s'
     ─────────────────────────────────────────────────────────── */

  /** 확대해도 이미지가 화면 밖으로 날아가지 않게 가둔다. */
  const clampTransform = useCallback((t: ViewTransform): ViewTransform => {
    const c = canvasRef.current;
    if (!c) return t;
    // offsetWidth 는 transform 의 영향을 받지 않는다 (변환 전 크기).
    const bw = c.offsetWidth, bh = c.offsetHeight;
    if (!bw || !bh) return t;
    const sw = bw * t.scale, sh = bh * t.scale;
    return {
      scale: t.scale,
      x: clamp(t.x, bw - sw, 0),
      y: clamp(t.y, bh - sh, 0),
    };
  }, []);

  /** 화면 좌표를 고정점으로 배율을 바꾼다. 미지정 시 캔버스 중앙. */
  const zoomAt = useCallback((factor: number, clientX?: number, clientY?: number) => {
    setTransform((t) => {
      const c = canvasRef.current;
      if (!c) return t;
      const r = c.getBoundingClientRect();
      const px = clientX ?? r.left + r.width / 2;
      const py = clientY ?? r.top + r.height / 2;
      const scale = clamp(t.scale * factor, 1, maxScale);
      if (scale === t.scale) return t;
      // 고정점은 캔버스 기준 좌표로 환산해야 한다 (rect 는 변환된 박스다).
      const ox = px - r.left + t.x;
      const oy = py - r.top + t.y;
      return clampTransform({
        scale,
        x: ox - ((ox - t.x) / t.scale) * scale,
        y: oy - ((oy - t.y) / t.scale) * scale,
      });
    });
  }, [maxScale, clampTransform]);

  const zoomIn = useCallback(() => zoomAt(1.4), [zoomAt]);
  const zoomOut = useCallback(() => zoomAt(1 / 1.4), [zoomAt]);

  /**
   * 휠 처리. **배율 1 일 때 평범한 휠은 건드리지 않는다** --
   * 호스트 페이지의 스크롤을 뺏으면 라이브러리로서 무례하다.
   *   Ctrl/⌘ + 휠 → 확대·축소 (트랙패드 핀치도 이 이벤트로 온다)
   *   확대된 상태에서 휠 → 이동
   */
  const onWheel = useCallback((e: WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      zoomAt(e.deltaY < 0 ? 1.12 : 1 / 1.12, e.clientX, e.clientY);
      return;
    }
    setTransform((t) => {
      if (t.scale === 1) return t;            // 확대 안 했으면 페이지에 양보
      e.preventDefault();
      return clampTransform({ ...t, x: t.x - e.deltaX, y: t.y - e.deltaY });
    });
  }, [zoomAt, clampTransform]);

  const toImage = useCallback((clientX: number, clientY: number): Point => {
    const r = rect.current;
    if (!r) return [0, 0];
    return [
      clamp(Math.round((clientX - r.left) * (engine.width / r.width)), 0, engine.width - 1),
      clamp(Math.round((clientY - r.top) * (engine.height / r.height)), 0, engine.height - 1),
    ];
  }, [engine]);

  const pinchState = () => {
    const [a, b] = [...pointers.current.values()];
    if (!a || !b) return null;
    return {
      dist: Math.hypot(b.x - a.x, b.y - a.y),
      mid: [(a.x + b.x) / 2, (a.y + b.y) / 2] as [number, number],
    };
  };

  const beginPinch = useCallback(() => {
    lasso.current = null;
    preview.current = null;
    const s = pinchState();
    if (s) pinch.current = { ...s, t: transform };
    schedule();
  }, [engine, transform, schedule]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!engine.loaded) return;
    // 가운데 버튼 드래그 = 이동. 그리기 도구를 바꾸지 않고 훑어볼 수 있게.
    if (e.button === 1 && transform.scale > 1) {
      e.preventDefault();
      panning.current = { x: e.clientX, y: e.clientY, t: transform };
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }

    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture(e.pointerId);

    if (pointers.current.size >= 2) { beginPinch(); return; }

    // 제스처당 한 번만 잰다. 매 이동마다 재면 강제 리플로우가 걸린다.
    rect.current = e.currentTarget.getBoundingClientRect();
    lasso.current = [toImage(e.clientX, e.clientY)];
    tapFrom.current = { x: e.clientX, y: e.clientY, max: 0 };
    preview.current = lasso.current;
    schedule();
  }, [engine, toImage, beginPinch, schedule, transform]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const pan = panning.current;
    if (pan) {
      setTransform(clampTransform({
        scale: pan.t.scale,
        x: pan.t.x + (e.clientX - pan.x),
        y: pan.t.y + (e.clientY - pan.y),
      }));
      return;
    }
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size >= 2) {
      const start = pinch.current;
      const now = pinchState();
      if (!start || !now || start.dist === 0) return;
      const scale = clamp(start.t.scale * (now.dist / start.dist), 1, maxScale);
      // transform-origin: 0 0 기준. 손가락 중점 아래의 내용이 그대로 있게 한다.
      setTransform(clampTransform({
        scale,
        x: now.mid[0] - ((start.mid[0] - start.t.x) / start.t.scale) * scale,
        y: now.mid[1] - ((start.mid[1] - start.t.y) / start.t.scale) * scale,
      }));
      return;
    }

    if (lasso.current) {
      const from = tapFrom.current;
      if (from) {
        from.max = Math.max(from.max, Math.hypot(e.clientX - from.x, e.clientY - from.y));
      }
      const p = toImage(e.clientX, e.clientY);
      const last = lasso.current[lasso.current.length - 1]!;
      if (Math.abs(p[0] - last[0]) + Math.abs(p[1] - last[1]) > MIN_MOVE) lasso.current.push(p);
      schedule();
    }
  }, [engine, maxScale, toImage, schedule, clampTransform]);

  const endPointer = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (panning.current) { panning.current = null; return; }
    pointers.current.delete(e.pointerId);
    if (pointers.current.size >= 2) return;
    if (pointers.current.size === 1) {
      // 손가락 하나가 떨어졌다. 남은 손가락으로 그리기 시작하면 원치 않는 획이 생긴다.
      pinch.current = null;
      return;
    }
    pinch.current = null;

    const pts = lasso.current;
    const from = tapFrom.current;
    lasso.current = null;
    tapFrom.current = null;
    preview.current = null;
    schedule();
    if (!pts) return;

    // 제스처 내내 거의 안 움직였으면 그리려던 게 아니라 누른 것이다.
    if ((from?.max ?? Infinity) <= TAP_SLOP) {
      void selectAt(pts[0]![0], pts[0]![1], lassoMode);
      return;
    }
    void selectLasso(pts, lassoMode);
  }, [engine, selectLasso, selectAt, lassoMode, schedule]);

  /**
   * 스테이지(캔버스를 감싸는 엘리먼트)에 펼친다.
   *
   * ⚠️ `style` 의 `transform` 은 **캔버스 엘리먼트에 직접** 써야 한다.
   * 캔버스는 코어가 만든 것이라 React 가 prop 으로 꽂지 못한다.
   * `<NukkiCanvas>` 가 그 일을 한다 — 직접 배선한다면 똑같이 해야 배율이 보인다.
   */
  const canvasProps = useMemo(() => ({
    onPointerDown, onPointerMove, onPointerUp: endPointer, onPointerCancel: endPointer,
    style: {
      touchAction: 'none' as const,
      transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
      transformOrigin: '0 0' as const,
    },
  }), [onPointerDown, onPointerMove, endPointer, transform]);

  return {
    engine, canvasRef, attachCanvas, canvasProps,
    loaded, busy, canUndo, canRedo, error, clearError,
    refine, setRefine, lassoMode, setLassoMode,
    transform, resetView, zoomIn, zoomOut, zoomAt, onWheel,
    canZoomIn: transform.scale < maxScale, canZoomOut: transform.scale > 1,
    selectionSteps, hasSelection: selectionSteps > 0,
    load, autoSelect, selectLasso, selectAt, applySelection, clearSelection, undoSelectionStep,
    undo, redo, reset, exportBlob,
  };
}

export type UseNukkiReturn = ReturnType<typeof useNukki>;
